export enum LocationSearchType {
    Screening = 0,
    RiskAssessment = 1,
    External = 2
}

export enum LocationSearchOrder {
    LocationNumber = 0,
    LocationName = 1,
    ScreeningLocationType = 2,
    RiskassessmentFactor = 3,
    ScreeningFactor = 4,
    ScreeningPollutant = 5,
    PollutantName = 6,
    RiskassessmentConcentration = 7,
    ScreeningConcentration = 8,
    RiskAssesmentStatus = 9,
    ScreeningStatus = 10,
    ScreeningAt = 11
}

export enum RiskCalculationSearchOrder {
    LocationNumber = 1,
    LocationName = 2,
    Factor = 3,
    CalculationModelType = 4,
    Status = 5,
    RiskAssessment = 6,
    CreatedAt = 7,
    CaseWorkerName = 8
}

export enum PolygonType {
    V1 = 0,
    V2 = 1
}

export enum ScreeningLocationType {
    V1 = 0,
    V2 = 1,
    V1_V2 = 2
}

export enum ScreeningPolygonStatus {
    NoRisk = 0,
    Risk = 1,
    OlieOgBenzinprodukter = 2
}

export enum HighestRiskCalculationType {
    None,
    Screening,
    RiskAssessment
}

export enum CalculationStatus {
    None,
    Calculating,
    AwaitingApproval,
    Approved,
    Deleted
}

export enum RiskAssessmentStatus {
    NoRisk = 0,
    Risk = 1,
}

export enum CalculationModelType {
    ModelI = 1,
    ModelII,
    ModelIII,
    ModelIV,
    ModelV,
}

export const CalculationModelNames: string[] = [
    '',
    'Homogen vandmættet ler',
    'Vandmættet opsprækket ler',
    'Umættet sand',
    'Umættet sand med impermeabelt dæklag',
    'Forureningskilde beliggende lige over grundvandet'
];

export enum CalculationType {
    Standard,
    V1AsV2Calculation,
    PreliminaryCalculation = 2
}

export enum LocationCalculationType {
    ApprovedCalculation = 1,
    UnapprovedCalculation = 2,
    V1AsV2Calculation = 3,
    ScreeningResult = 4,
    PreliminaryResult = 5,
    PreliminaryCalculation = 6
}

export enum Degradation {
    None = 1,
    Simple,
    Sequential
}

export enum ViewCalculationMode {
    ShowAllCalculations,
    ShowMineCalculations,
}

export const CalculationDegradationNames: string[] = [
    '',
    'Ingen nedbrydning',
    '1. ordens nedbrydning',
    'Sekventiel 1. ordens nedbrydning'
];

export class ModelDefaultValues {
    public verticalLongitudinalDispersivity: number;
    public verticalTransversalDispersivity: number;
    public bulkHydraulicConductivity: number;
    public fractureSpacing: number;
    public horizontalLongitudinalDispersivity: number;
    public horizontalTransversalDispersivity: number;
    public horizontalVerticalDispersivity: number;

    public constructor(init: Partial<ModelDefaultValues>) {
        Object.assign(this, init);
    }
}

export enum MagasinType {
    quaternarySand,
    preQuaternarySand,
    crackedLime
}

export enum GroundType {
    FedLer,
    MoræneLer,
    Sand,
    Silt,
    SandOgGrus
}

export enum CommentCategory {
    // Comments added on the result page. No header text
    None,

    // Input parameter groups
    PollutionSource,
    VerticalTransport,
    HorizontalTransport,
    PointsOfCompliance,

    // Comment added when (un)approving a calculation
    CalculationApproval,
    CalculationUnapproval,
}

export const CommentCategoryHeaderText: Map<CommentCategory, string> = new Map([
    [CommentCategory.None, ''],
    [CommentCategory.PollutionSource, 'Kommentar til forureningskilde'],
    [CommentCategory.VerticalTransport, 'Kommentar til vertikal transport'],
    [CommentCategory.HorizontalTransport, 'Kommentar til horisontal transport'],
    [CommentCategory.PointsOfCompliance, 'Kommentar til kontrolpunkter'],
    [CommentCategory.CalculationApproval, 'Kommentar til godkendelse'],
    [CommentCategory.CalculationUnapproval, 'Kommentar til fjern godkendelse'],
]);

export enum ScreeningStatus {
    NotStarted = 0,
    InProgress = 1,
    Done = 2
}

export enum DataQuality {
    Weak, Medium, Strong
}

export enum ScreeningFlag {
    None = 0,
    Bly_kobber_eller_PAH = 1,
    Kompleks_geologi = 2,
    Manglende_modelstof = 4,
    Svag_geologi = 8,
    MTBE_fjernet = 16,
    Boring_uden_ler = 32,
    Risiko_pga_vandindvinding = 64,
    Kun_Olie_Benzin_Stoffer = 128
}

export enum ReassessmentType {
    RiskToNoRisk = 0,
    NoRiskToRisk = 1,
    PreliminaryRiskAssessment = 2
}

export enum ScreeningLogInfo {
    CoverLayerAdequate,
    CoverLayerAdequateForMetals,
    CoverLayerInadequate,
    CoverLayerIgnoredForPesticides,
    CoverLayerInadequateForMetals,
    CoverLayerDegradation,
    NoQualityCriterion
}

export enum RemovalReason {
    NotRemoved = 0, // default value

    Removed_0_1 = 1, // 'MTBE removed from V2 if OM'
    Removed_0_2 = 2, // 'MTBE_removed_red_brancher_and_activities'

    Removed_1_1 = 11, // '1.1
    Removed_1_2 = 12, // '1.2 removed PAH,bly,Kobber,NVOC,COD,Ammonium'
    Removed_1_3 = 13, // '1.3 removed zero kildestorrelse'

    Removed_2_1 = 21, // '2.1 removed green tables'
    Removed_2_2 = 22, // '2.2 removed without compound code'
    Removed_2_3 = 23, // '2.3 GW criteria after vertical tranpsort'
    Removed_2_4 = 24, // 2.1 removed MTBE due to geology

    Removed_3_1 = 31, // '3.1 GW criteria after horizontal tranpsort'
}

export const RemovalReasonTexts: Map<RemovalReason, string> = new Map([
    [RemovalReason.NotRemoved, '-'],

    [RemovalReason.Removed_0_1, 'MTBE fjernet ifm. olie-miljø-pulje'],
    [RemovalReason.Removed_0_2, 'MTBE fjernet, grundet aktiviteter vedr. værksted/olie'],

    [RemovalReason.Removed_1_1, 'Fjernet grundet modelstof NVOC/COD/Ammonium/PAH/Bly/Kobber'],
    [RemovalReason.Removed_1_2, 'Fjernet grundet stofgruppe PAH/Bly/Kobber'],
    [RemovalReason.Removed_1_3, 'Forureningsfladen er 0'],

    [RemovalReason.Removed_2_1, 'Grundet dæklagets tykkelse forventes det ikke, at forureningsstoffet når grundvandet'],
    [RemovalReason.Removed_2_2, 'Fjernet grundet bestemte stoffer'],
    [RemovalReason.Removed_2_3, 'GVK er større end koncentration efter vertikal transport'],
    [RemovalReason.Removed_2_4, 'MTBE fjernet grundet dæklagstykkelsen'],

    [RemovalReason.Removed_3_1, 'GVK er større end koncentration efter horisontalt transport']
]);

export enum LocationOrigin {
    Screening = 1,
    NoModelCompoundsFound = 2,
    PublicAction = 3,
    V1asV2Calculation = 4,
    External = 5
}

export enum LocationSearchCacheKey {
    Screening = 'ScreeningLocationSearch',
    RiskAssessment = 'ScreeningLocationSearch',
    ScreeningCompound = 'ScreeningCompoundsSearch',
    Compound = 'RiskAssessmentCompoundsSearch',
    RiskCalculation = 'RiskCalculationSearch'
}

export enum CompoundOrigin {
    Dkjord = 0,
    JAR = 1,
    Landfill = 2
}

export enum CompoundTranslationType {
    Activity = 0,
    PollutionCause = 1,
    Pollutant = 2
}

export enum CompoundTranslationTypeGroupKey {
    Brancher = 'Brancher',
    Stoffer = 'Stoffer',
    Aktiviteter = 'Aktiviteter'
}

export enum OverrulingScreeningType {
    None = 0,
    ExceedingFactorChangedToLargerOrEqualOneRule = 10,
    ExceedingFactorChangedToSmallerThanOneRule = 20,
    ExceedingFactorExceedLimitationRule = 30,
    FlagsChangedRule = 40,
    PollutantsChangedRule = 50,
    PollutionCausesChangedRule = 60,
    ActivitiesChangedRule = 70,
    SumV1PolygonAreaExceedLimitationRule = 80,
    SumV2PolygonAreaExceedLimitationRule = 90,
    MissingModelCompoundsChangedRule = 100
}

export enum ReasonToSaveScreening {
    None = 0,
    FirstTimeSaved = 1,
    AppliedOverrulingScreening = 10
}

export enum MissingActivityType
{
    MissingCompound = 1,
    MissingModelCompound = 2
}

export enum MissingPollutionCauseType
{
    MissingCompound = 1,
    MissingModelCompound = 2
}

export enum ScreeningTriggeredBy
{
    Dkjord = 0,
    GrundriskRescreening = 1
}

export enum QualityCriterionType
{
    None = 0,
    GVK = 1,
    DVK = 2,
    Fictitious = 3
}

export enum ConcentrationOrigin {
    None = 0,
    Standard = 1,
    PollutionCause = 2,
    Activity = 3
}

export enum CalculationCompounds {
    Benzen = 'Benzen',
    Toluen = 'Toluen',
    Ethylbenzen = 'Ethylbenzen',
    Xylen = 'Xylen',
    Naphthalen = 'Naphthalen',
    Kulbrinter = 'Kulbrinter'
}

// The below standat lists depend on CalculationCompound.csv file on our system.
export const CalculationCompoundStandatMap: Map<CalculationCompounds, number[]> = new Map([
    [CalculationCompounds.Benzen, [662]],
    [CalculationCompounds.Toluen, [665]],
    [CalculationCompounds.Ethylbenzen, [3007]],
    [CalculationCompounds.Xylen, [668, 2660, 2661, 2662]],
    [CalculationCompounds.Naphthalen, [649]],
    [CalculationCompounds.Kulbrinter, [9508]],
]);

export enum PolygonGeusParameter {
    PolygonPollutionStatus = 'pollutionStatus',
    Area = 'area',
}

export enum GeusParameterNames {
    DataQuality = 'dataQuality',
    CoverThickness = 'coverThickness',
    AquiferDepth = 'aquiferDepth',
    Groundwater = 'groundWater',
    Elevation = 'elevation',
    Flowdir = 'flowdir',
    HeadGradient = 'headGradient',
    Infiltration = 'infiltration',
    LithoName = 'lithoName',
    Redox = 'redox',
    DistNoClay = 'distNoClay',
    DistNearestWaterWell = 'distNearestWaterWell',
    UpdateDate = 'updateDate',
}

export enum GeusParameterFlags {
    ComplexGeology = 'complexGeology',
    HasBoringWithoutClay = 'hasBoringWithoutClay',
}

export const GeusParameterDanishNames: Map<GeusParameterNames, string> = new Map([
    [GeusParameterNames.DataQuality, 'Datakvalitet'],
    [GeusParameterNames.CoverThickness, 'Dæklagtykkelse'],
    [GeusParameterNames.AquiferDepth, 'Dybde til grundvandsmagasin'],
    [GeusParameterNames.Groundwater, 'Dybde til grundvand'],
    [GeusParameterNames.Elevation, 'Elevation'],
    [GeusParameterNames.Flowdir, 'Flow retning'],
    [GeusParameterNames.HeadGradient, 'Hydraulisk gradient'],
    [GeusParameterNames.Infiltration, 'Infiltration'],
    [GeusParameterNames.LithoName, 'Litho navn'],
    [GeusParameterNames.Redox, 'Redox'],
    [GeusParameterNames.DistNoClay, 'Afstand til nærmeste boring uden ler i dæklag'],
    [GeusParameterNames.DistNearestWaterWell, 'Afstand til nærmeste indvinding'],
]);

export const GeusParameterFlagsDanishNames: Map<GeusParameterFlags, string> = new Map([
    [GeusParameterFlags.ComplexGeology, 'Kompleks geologi'],
    [GeusParameterFlags.HasBoringWithoutClay, 'Boring uden ler'],
]);

export const PolygonGeusParameterDanishNames: Map<PolygonGeusParameter, string> = new Map([
    [PolygonGeusParameter.PolygonPollutionStatus, 'Flade status'],
    [PolygonGeusParameter.Area, 'Flader areal'],
]);

